import React, { useState } from 'react';
import {
    Grid,
    TextField,
    Button,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

const getDiscountStatusOptions = () => [
    { key: '1', value: 'Zatwierdzony' },
    { key: '11', value: 'Niezatwierdzony' },
    { key: '21', value: 'Zrealizowany' }
];

const getStatusText = (statusId) => {
    const status = getDiscountStatusOptions().find(opt => opt.key === String(statusId));
    return status ? status.value : 'Nieznany';
};

const initialForm = {
    id: null,
    tempId: null,
    discountAmount: '',
    invoiceDescription: '',
    internalComment: '',
    type: 'DISCOUNT'
};

const DiscountComponent = ({ contactId, userName, discounts, setDiscounts, roles }) => {

    const [formState, setFormState] = useState(initialForm);
    const [isEditing, setIsEditing] = useState(false);

    const isDiscountCreatable =
        roles?.includes('ADMIN') ||
        roles?.includes('ADMIN_SUPERVISOR') ||
        roles?.includes('SUPERVISOR') ||
        roles?.includes('SUPERVISOR_ACCOUNTANT') ||
        roles?.includes('MANAGER') ||
        roles?.includes('ACCOUNT_MANAGER');

    const isDiscountEditable =
        roles?.includes('ADMIN') ||
        roles?.includes('ADMIN_SUPERVISOR') ||
        roles?.includes('SUPERVISOR') ||
        roles?.includes('SUPERVISOR_ACCOUNTANT') ||
        roles?.includes('MANAGER') ||
        roles?.includes('ACCOUNT_MANAGER');

    const isApprovedDiscountEditable =
        roles?.includes('ADMIN') ||
        roles?.includes('ADMIN_SUPERVISOR') ||
        roles?.includes('MANAGER');

    const canEditOrDelete = (discount) =>
        (discount.statusId === 11 && isDiscountEditable) ||
        (discount.statusId === 1 && isApprovedDiscountEditable);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddClick = (type) => {
        setFormState({
            ...initialForm,
            type
        });
        setIsEditing(true);
    };

    const handleCancel = () => {
        setFormState(initialForm);
        setIsEditing(false);
    };

    const handleSubmit = () => {

        const { discountAmount, invoiceDescription, type } = formState;

        if (!discountAmount || !invoiceDescription) {
            alert('Uzupełnij wymagane pola.');
            return;
        }

        const amount = parseFloat(discountAmount);

        if (type === 'DISCOUNT' && amount > 700) {
            alert('Kwota rabatu nie może przekraczać 700 PLN netto.');
            return;
        }

        if (formState.id != null || formState.tempId != null) {

            setDiscounts(discounts.map(d => {

                const isSame = d.id === formState.id || d.tempId === formState.tempId;

                return isSame
                    ? {
                        ...d,
                        discountAmount: amount,
                        invoiceDescription: formState.invoiceDescription,
                        internalComment: formState.internalComment,
                        type
                    }
                    : d;

            }));

        } else {

            const newDiscount = {
                id: null,
                tempId: uuidv4(),
                discountAmount: amount,
                invoiceDescription: formState.invoiceDescription,
                internalComment: formState.internalComment,
                type,
                statusId: 11,
                assignedById: contactId,
                approvedById: null,
                assignedByName: null,
                approverName: null
            };

            setDiscounts([...discounts, newDiscount]);
        }

        handleCancel();
    };

    const handleEdit = (discount) => {

        if (!canEditOrDelete(discount)) return;

        setFormState({
            id: discount.id,
            tempId: discount.tempId || null,
            discountAmount: discount.discountAmount,
            invoiceDescription: discount.invoiceDescription,
            internalComment: discount.internalComment,
            type: discount.type
        });

        setIsEditing(true);
    };

    const handleDelete = (idOrTempId, discount) => {

        if (!canEditOrDelete(discount)) return;

        const confirm = window.confirm('Czy na pewno chcesz usunąć pozycję?');

        if (confirm) {
            setDiscounts(discounts.filter(d =>
                d.id !== idOrTempId && d.tempId !== idOrTempId
            ));
        }
    };

    const getTypeText = (type) => type === 'DISCOUNT' ? 'Rabat' : 'Dopłata';

    const getTypeColor = (type) => type === 'DISCOUNT' ? 'green' : 'red';

    return (

        <Box>

            {!isEditing && isDiscountCreatable && (

                <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>

                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleAddClick('DISCOUNT')}
                    >
                        Dodaj rabat
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleAddClick('SURCHARGE')}
                    >
                        Dodaj dopłatę
                    </Button>

                </Box>

            )}

            {isEditing && (

                <Box sx={{ mb: 2 }}>

                    <Grid container spacing={2}>

                        <Grid item xs={3}>
                            <TextField
                                name="discountAmount"
                                label={
                                    formState.type === 'DISCOUNT'
                                        ? 'Kwota rabatu netto'
                                        : 'Kwota dopłaty netto'
                                }
                                value={formState.discountAmount}
                                onChange={handleChange}
                                fullWidth
                                type="number"
                                inputProps={{
                                    step: 0.01,
                                    min: 0
                                }}
                            />
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                name="invoiceDescription"
                                label="Opis na fakturze"
                                value={formState.invoiceDescription}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={5}>
                            <TextField
                                name="internalComment"
                                label="Komentarz"
                                value={formState.internalComment}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>

                    </Grid>

                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                        >
                            {formState.id != null || formState.tempId != null
                                ? 'Zapisz zmiany'
                                : formState.type === 'DISCOUNT'
                                    ? 'Przyznaj rabat'
                                    : 'Dodaj dopłatę'}
                        </Button>

                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleCancel}
                        >
                            Anuluj
                        </Button>

                    </Box>

                </Box>

            )}

            <TableContainer component={Paper}>

                <Table size="small">

                    <TableHead>

                        <TableRow>

                            <TableCell>Typ</TableCell>
                            <TableCell>Kwota netto</TableCell>
                            <TableCell>Opis na fakturze</TableCell>
                            <TableCell>Komentarz</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Kto przydzielił</TableCell>
                            <TableCell>Kto zatwierdził</TableCell>
                            <TableCell>Data utworzenia</TableCell>
                            <TableCell>Akcje</TableCell>

                        </TableRow>

                    </TableHead>

                    <TableBody>

                        {discounts.map((discount) => {

                            const disabled = !canEditOrDelete(discount);

                            return (

                                <TableRow key={discount.id ?? discount.tempId}>

                                    <TableCell>

                                        <Box sx={{
                                            color: getTypeColor(discount.type),
                                            fontWeight: 600
                                        }}>
                                            {getTypeText(discount.type)}
                                        </Box>

                                    </TableCell>

                                    <TableCell>
                                        {discount.discountAmount} zł
                                    </TableCell>

                                    <TableCell>
                                        {discount.invoiceDescription}
                                    </TableCell>

                                    <TableCell>
                                        {discount.internalComment}
                                    </TableCell>

                                    <TableCell>
                                        {getStatusText(discount.statusId)}
                                    </TableCell>

                                    <TableCell>
                                        {discount.assignedByName || userName}
                                    </TableCell>

                                    <TableCell>
                                        {discount.approverName || '—'}
                                    </TableCell>

                                    <TableCell>
                                        {discount.createdOn || new Date().toISOString().split('T')[0]}
                                    </TableCell>

                                    <TableCell>

                                        <IconButton
                                            onClick={() => handleEdit(discount)}
                                            disabled={disabled}
                                        >
                                            <Edit />
                                        </IconButton>

                                        <IconButton
                                            onClick={() => handleDelete(discount.id ?? discount.tempId, discount)}
                                            disabled={disabled}
                                        >
                                            <Delete />
                                        </IconButton>

                                    </TableCell>

                                </TableRow>

                            );

                        })}

                        {discounts.length === 0 && (

                            <TableRow>

                                <TableCell colSpan={9} align="center">
                                    Brak rabatów / dopłat
                                </TableCell>

                            </TableRow>

                        )}

                    </TableBody>

                </Table>

            </TableContainer>

        </Box>

    );
};

export default DiscountComponent;